import type { DesktopTextDocument } from "@rakazo/contracts";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { desktopBridge } from "../lib/desktop";
import {
  clearEditorTabs,
  WorkspaceScopeContext,
  WorkspaceWindowIdContext,
} from "../lib/workspace-local-state";

class WindowDocuments extends Map<string, DesktopTextDocument> {
  private closed = false;

  get isClosed() {
    return this.closed;
  }

  override set(resource: string, document: DesktopTextDocument) {
    // A native picker/save may finish after its window was closed.
    if (this.closed) void desktopBridge()?.editor?.release(document.id);
    else super.set(resource, document);
    return this;
  }

  reopen() {
    this.closed = false;
  }

  close() {
    this.closed = true;
    for (const document of this.values()) void desktopBridge()?.editor?.release(document.id);
    this.clear();
  }
}

const Context = createContext<Map<string, WindowDocuments> | null>(null);

/** Native file grants survive minimization and belong to one concrete editor window. */
export function NativeEditorDocuments({ children }: { children: ReactNode }) {
  const scope = useContext(WorkspaceScopeContext);
  const [windows] = useState(() => new Map<string, WindowDocuments>());
  useEffect(() => {
    const close = (event: Event) => {
      const id: unknown = (event as CustomEvent).detail;
      if (typeof id !== "string") return;
      windows.get(id)?.close();
      windows.delete(id);
      clearEditorTabs(scope, id);
    };
    window.addEventListener("workspace-closed", close);
    return () => {
      window.removeEventListener("workspace-closed", close);
      for (const documents of windows.values()) documents.close();
      windows.clear();
    };
  }, [windows, scope]);
  return <Context.Provider value={windows}>{children}</Context.Provider>;
}

export function useNativeEditorDocuments() {
  const windows = useContext(Context);
  const windowId = useContext(WorkspaceWindowIdContext) ?? "editor";
  const [documents] = useState(() => {
    if (!windows) return null;
    let held = windows.get(windowId);
    if (!held) {
      held = new WindowDocuments();
      windows.set(windowId, held);
    }
    return held;
  });
  useEffect(() => {
    if (!documents || !windows) return;
    // Re-register on React effect replay; hiding a window does not release its grants.
    documents.reopen();
    windows.set(windowId, documents);
  }, [documents, windows, windowId]);
  return documents;
}
